import React from 'react';
import { Link } from 'react-router-dom';
import { usePagesTextes, TexteRiche } from '../hooks/useFormTextes';

const HowToRegister = () => {
  const T = usePagesTextes(); // Iter 767 — textes FAQ éditables
  return (
    <div className="how-to-register-page">
      {/* Hero */}
      <section className="bg-gradient-to-r from-[#79c9ea] to-[#5ba8c8] text-white py-16">
        <div className="container-custom text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 uppercase tracking-wide">FORMULAIRE D'INSCRIPTION</h1>
          <p className="text-xl opacity-90">Toute inscription commence par ce formulaire en ligne</p>
          <p className="text-base opacity-75 mt-2">C'est la base, que vous soyez à l'aise avec Internet ou non</p>
        </div>
      </section>

      {/* Section unique : Formulaire d'inscription */}
      <section className="section">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <div className="card p-8 hover:shadow-2xl transition">
              <div className="text-center mb-6">
                <svg className="w-14 h-14 mx-auto text-[#5BC5D4] mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                <h2 className="text-2xl font-bold mb-2 text-[#5BC5D4]">REMPLISSEZ LE FORMULAIRE EN LIGNE</h2>
                <p className="text-gray-600">Le formulaire est l'unique porte d'entrée pour valider votre inscription.</p>
              </div>

              <div className="space-y-4 mb-6 max-w-xl mx-auto">
                <div className="flex items-start gap-3"><span className="text-[#5BC5D4] text-xl font-bold">1.</span><p className="text-gray-700">Remplissez le formulaire d'inscription en ligne (nous vous accompagnons à chaque étape)</p></div>
                <div className="flex items-start gap-3"><span className="text-[#5BC5D4] text-xl font-bold">2.</span><p className="text-gray-700">Vous recevez immédiatement une confirmation par email avec votre récapitulatif en PDF</p></div>
                <div className="flex items-start gap-3"><span className="text-[#5BC5D4] text-xl font-bold">3.</span><p className="text-gray-700"><strong>Dans les 24 heures</strong>, depuis votre <strong>Espace Adhérent</strong> (il s'ouvre automatiquement après l'envoi) : soit vous réglez en ligne, soit vous prenez un rendez-vous pour régler sur place</p></div>
              </div>

              <a href="/inscription"
                className="flex items-center justify-center gap-3 bg-gradient-to-r from-[#5BC5D4] to-[#4AB4C3] text-white px-8 py-5 rounded-xl font-bold text-lg hover:shadow-xl transition-all transform hover:scale-105 w-full">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                <span>FORMULAIRE D'INSCRIPTION</span>
              </a>

              <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800 font-bold">
                  Dans les 24 heures suivant l'envoi du formulaire, vous devez impérativement soit effectuer votre paiement en ligne,
                  soit prendre un rendez-vous pour régler sur place. Sans règlement ou sans rendez-vous, l'inscription ne sera pas validée.
                </p>
              </div>
            </div>

            {/* Note discrète : rendez-vous pour paiement + accompagnement optionnel */}
            <div className="mt-6 text-center text-sm text-gray-600 leading-relaxed" data-testid="rdv-note">
              <p>
                <strong>À quoi sert le rendez-vous ?</strong> Uniquement à <strong>régler en espèces ou en chèques-vacances papier</strong> à la permanence (Decathlon Bron, 10 min). Pas de carte bancaire sur place : carte, virement et chèques-vacances par courrier se font <strong>depuis votre Espace Adhérent</strong>.{' '}
                <Link to="/payer" className="text-[#79c9ea] font-bold underline hover:text-[#5ba8c8]">Voir « Régler mon voyage »</Link>.
              </p>
              <p className="mt-2 text-gray-500 text-xs italic">
                Vous n'êtes pas à l'aise avec Internet ? Pas de souci : vous pouvez remplir le formulaire avec nous pendant le rendez-vous.
                Dans ce cas, <a href="https://wa.me/message/XTYPH2HUY3L6L1" target="_blank" rel="noopener noreferrer" className="text-[#25D366] font-bold underline">contactez-nous par WhatsApp</a> pour convenir d'un moment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Accompagnement administratif */}
      <section className="section bg-gray-50" data-testid="admin-help-section">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold mb-3 text-[#1E3A5F] text-center">Accompagnement administratif et sanitaire</h2>
            <p className="text-center text-gray-600 mb-8">Nous ne nous contentons pas de vendre un voyage : nous aidons aussi à préparer le dossier.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="card p-6">
                <h3 className="font-bold text-lg text-[#1E3A5F] mb-3">Formalités administratives</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-[#5BC5D4] font-bold mt-0.5">&#10003;</span>
                    <span>Vérifier la validité de votre carte d'identité ou passeport</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#5BC5D4] font-bold mt-0.5">&#10003;</span>
                    <span>Comprendre si un visa ou autorisation (ESTA, e-visa...) est nécessaire</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#5BC5D4] font-bold mt-0.5">&#10003;</span>
                    <span>Remplir ensemble les formulaires en ligne, pas à pas</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#5BC5D4] font-bold mt-0.5">&#10003;</span>
                    <span>Rassembler les documents demandés par les compagnies</span>
                  </li>
                </ul>
              </div>
              <div className="card p-6">
                <h3 className="font-bold text-lg text-[#1E3A5F] mb-3">Conseils sanitaires</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-[#5BC5D4] font-bold mt-0.5">&#10003;</span>
                    <span>Repérer les vaccins conseillés pour votre destination</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#5BC5D4] font-bold mt-0.5">&#10003;</span>
                    <span>Conseils trousse à pharmacie, précautions alimentaires et climatiques</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#5BC5D4] font-bold mt-0.5">&#10003;</span>
                    <span>Préparer les attestations et documents d'assurance</span>
                  </li>
                </ul>
                <div className="mt-4 bg-green-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600">
                    Tout cela se fait principalement par <strong>WhatsApp</strong> (messages, vocaux, pièces jointes), complété si besoin par téléphone ou rendez-vous.
                    <strong> Vous n'êtes pas seul face aux démarches.</strong>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Adhésion annuelle */}
      <section className="section" data-testid="adhesion-section">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="card p-6 bg-[#F0FAFB] border-l-4 border-[#5BC5D4]">
              <h3 className="font-bold text-lg text-[#1E3A5F] mb-2">Adhésion annuelle</h3>
              <p className="text-sm text-gray-700">
                Comme dans de nombreuses structures associatives, une adhésion annuelle au montant modeste est demandée.
                Elle est valable <strong>par foyer</strong> et contribue au fonctionnement général de l'association.
                L'inscription devient définitive lorsque le formulaire est rempli et qu'un premier règlement a été enregistré.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section Assurance avec Groupama */}
      <section className="section">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-4xl font-bold mb-4 text-[#1E3A5F]">🛡️ Protégez votre voyage avec notre assurance</h2>
              <p className="text-xl text-gray-600">En partenariat avec Groupama</p>
            </div>

            {/* Intro */}
            <div className="card p-8 mb-8 bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200">
              <h3 className="text-2xl font-bold mb-4 text-[#1E3A5F]">✨ Pourquoi souscrire à l'assurance ?</h3>
              <p className="text-gray-700 text-lg mb-4">
                Grâce à notre partenariat avec <strong>Groupama</strong>, bénéficiez d'une couverture complète pour votre voyage à des tarifs préférentiels. 
                L'assurance vous protège contre les imprévus et vous permet de voyager l'esprit tranquille.
              </p>
              <div className="bg-white rounded-lg p-4 mt-4">
                <p className="text-gray-700">
                  <strong>💡 Bon à savoir :</strong> L'assurance n'est pas obligatoire, mais fortement recommandée pour vous protéger en cas d'annulation, d'accident ou de maladie.
                </p>
              </div>
            </div>

            {/* Garanties principales */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {/* Garantie Annulation */}
              <div className="card p-6 hover:shadow-xl transition-all">
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-4xl">❌</div>
                  <h3 className="text-2xl font-bold text-[#5BC5D4]">Garantie Annulation</h3>
                </div>
                <p className="text-gray-700 mb-4">
                  Remboursement des sommes engagées si vous devez annuler votre voyage avant le départ pour l'un des motifs couverts :
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 font-bold">✓</span>
                    <span>Maladie grave, accident ou décès (vous, votre famille proche)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 font-bold">✓</span>
                    <span>Complications de grossesse</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 font-bold">✓</span>
                    <span>Licenciement économique</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 font-bold">✓</span>
                    <span>Refus de visa, vol de documents (48h avant départ)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 font-bold">✓</span>
                    <span>Destruction de locaux (&gt;50%), convocation judiciaire</span>
                  </li>
                </ul>
                <div className="mt-4 p-3 bg-yellow-50 rounded">
                  <p className="text-sm text-gray-700">
                    <strong>Franchise :</strong> 10% du montant (min. 40€, max. 100€)
                  </p>
                </div>
              </div>

              {/* Autres Garanties */}
              <div className="card p-6 hover:shadow-xl transition-all">
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-4xl">🏥</div>
                  <h3 className="text-2xl font-bold text-[#5BC5D4]">Autres Garanties</h3>
                </div>
                <p className="text-gray-700 mb-4">
                  Couverture complète pendant votre voyage :
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 font-bold">✓</span>
                    <span><strong>Assistance rapatriement 24h/24</strong> en cas de maladie ou accident</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 font-bold">✓</span>
                    <span><strong>Frais médicaux à l'étranger</strong> (hospitalisation, soins, dentaire d'urgence)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 font-bold">✓</span>
                    <span><strong>Perte ou vol de bagages</strong> (remboursement)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 font-bold">✓</span>
                    <span><strong>Interruption de voyage</strong> (remboursement frais non consommés)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 font-bold">✓</span>
                    <span><strong>Protection accident corporel</strong> (capital décès/invalidité)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 font-bold">✓</span>
                    <span><strong>Avance de fonds</strong> en cas de perte ou vol</span>
                  </li>
                </ul>
                <div className="mt-4 p-3 bg-blue-50 rounded">
                  <p className="text-sm text-gray-700">
                    <strong>📞 Assistance :</strong> 00 33 1 45 16 66 05 (24h/24, 7j/7)
                  </p>
                </div>
              </div>
            </div>

            {/* Téléchargements */}
            <div className="card p-8 bg-gradient-to-br from-[#5BC5D4] to-[#4AB4C3] text-white">
              <h3 className="text-2xl font-bold mb-4 text-center">📄 Documents à télécharger</h3>
              <p className="text-center mb-6 text-lg opacity-90">
                Consultez le détail complet des garanties et conditions d'assurance
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <a
                  href="https://customer-assets.emergentagent.com/job_fr-application/artifacts/7qse8mat_GARANTIES_ANNULATION_AVANT_DEPART.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-3 bg-white text-[#5BC5D4] px-6 py-4 rounded-xl font-bold hover:bg-gray-50 transition-all shadow-lg hover:shadow-xl"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Garanties Annulation
                </a>
                <a
                  href="https://customer-assets.emergentagent.com/job_fr-application/artifacts/d7mrj9bg_AUTRES_GARANTIES_DU_CONTRAT_D_ASSURANCE_MULTIRISQUES.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-3 bg-white text-[#5BC5D4] px-6 py-4 rounded-xl font-bold hover:bg-gray-50 transition-all shadow-lg hover:shadow-xl"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Autres Garanties
                </a>
              </div>
            </div>

            {/* Note importante */}
            <div className="card p-6 mt-6 bg-yellow-50 border-2 border-yellow-300">
              <h4 className="font-bold text-lg mb-2 text-yellow-900">⚠️ Important</h4>
              <ul className="space-y-2 text-gray-700">
                <li>• L'assurance doit être souscrite <strong>au moment de l'inscription</strong></li>
                <li>• Le coût de l'assurance est calculé en fonction du montant de votre voyage</li>
                <li>• Certaines exclusions s'appliquent - consultez les documents pour les détails</li>
                <li>• En cas d'assistance, appelez <strong>immédiatement</strong> le numéro d'urgence</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Iter 361 (Kamel) — Dépliant : comprendre les chambres et l'échéancier */}
      <section className="section bg-white">
        <div className="container-custom max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold mb-8 text-[#1E3A5F] text-center">🛏️ Comprendre les chambres et l'échéancier</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="card p-6">
              <h3 className="font-bold text-lg text-[#1E3A5F] mb-3" data-testid="faq-chambres-titre">{T('faq_chambres_titre')}</h3>
              <TexteRiche texte={T('faq_chambres')} className="text-gray-700 text-sm leading-relaxed space-y-2" liClassName="space-y-2" />
            </div>
            <div className="card p-6">
              <h3 className="font-bold text-lg text-[#1E3A5F] mb-3" data-testid="faq-echeancier-titre">{T('faq_echeancier_titre')}</h3>
              <TexteRiche texte={T('faq_echeancier')} className="text-gray-700 text-sm leading-relaxed space-y-2" liClassName="space-y-2" />
            </div>
          </div>
        </div>
      </section>

      {/* Modes de paiement */}
      <section className="section bg-gray-50">
        <div className="container-custom">
          <h2 className="text-4xl font-bold mb-12 text-[#1E3A5F] text-center">Modes de Paiement Acceptés</h2>
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="card p-6">
                <div className="text-4xl mb-3">💳</div>
                <h3 className="text-xl font-bold mb-2 text-[#1E3A5F]">Carte Bancaire</h3>
                <p className="text-gray-600">Paiement en ligne sécurisé via HelloAsso</p>
                <p className="text-sm text-gray-500 mt-2">⚠️ Supprimez les frais de plateforme en cliquant sur "modifier"</p>
              </div>

              <div className="card p-6">
                <div className="text-4xl mb-3">🏦</div>
                <h3 className="text-xl font-bold mb-2 text-[#1E3A5F]">Virement Bancaire</h3>
                <p className="text-gray-600">IBAN : <strong className="text-[#5BC5D4]">FR76 1780 6002 8504 1611 8122 104</strong></p>
                <p className="text-sm text-gray-500 mt-2">Au nom de : Rhone amitié</p>
                <p className="text-sm text-yellow-600 mt-2">📸 Envoyez-nous le justificatif + précisez le voyage</p>
              </div>

              <div className="card p-6">
                <div className="text-4xl mb-3">💵</div>
                <h3 className="text-xl font-bold mb-2 text-[#1E3A5F]">Espèces</h3>
                <p className="text-gray-600">Lors d'un rendez-vous physique</p>
              </div>

              <div className="card p-6">
                <div className="text-4xl mb-3">🎫</div>
                <h3 className="text-xl font-bold mb-2 text-[#1E3A5F]">Chèques Vacances</h3>
                <p className="text-gray-600">Acceptés en rendez-vous</p>
              </div>

              <div className="card p-6">
                <div className="text-4xl mb-3">📝</div>
                <h3 className="text-xl font-bold mb-2 text-[#1E3A5F]">Chèque Bancaire</h3>
                <p className="text-gray-600">Contactez-nous pour les modalités</p>
              </div>

              <div className="card p-6">
                <div className="text-4xl mb-3">🎟️</div>
                <h3 className="text-xl font-bold mb-2 text-[#1E3A5F]">Avoir</h3>
                <p className="text-gray-600">Valable 2 ans sur un autre voyage</p>
              </div>
            </div>

            <div className="card p-6 mt-8 bg-green-50 border-2 border-green-200">
              <h3 className="text-xl font-bold mb-3 text-green-800">✅ Paiement en 10 fois sans frais</h3>
              <p className="text-gray-700">Possibilité de payer en plusieurs fois avant le départ, sans frais supplémentaires !</p>
            </div>
          </div>
        </div>
      </section>

      {/* Échéancier et Conditions */}
      <section className="section">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto space-y-8">
            {/* Échéancier */}
            <div className="card p-8">
              <h2 className="text-3xl font-bold mb-6 text-[#1E3A5F]">📅 Échéancier de Paiement</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                  <span className="text-2xl">1️⃣</span>
                  <div>
                    <h4 className="font-bold text-lg mb-1">À l'inscription</h4>
                    <p className="text-gray-700">20% minimum du forfait (50% si moins de 6 mois avant le départ)</p>
                    <p className="text-gray-700">+ Adhésion annuelle + Assurance si souscrite</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                  <span className="text-2xl">2️⃣</span>
                  <div>
                    <h4 className="font-bold text-lg mb-1">6 mois avant le départ</h4>
                    <p className="text-gray-700">50% minimum du forfait doit être réglé</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                  <span className="text-2xl">3️⃣</span>
                  <div>
                    <h4 className="font-bold text-lg mb-1">45 jours avant le départ</h4>
                    <p className="text-gray-700">Solde total du voyage doit être réglé</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Conditions d'annulation */}
            <div className="card p-8 border-2 border-red-200">
              <h2 className="text-3xl font-bold mb-6 text-red-700">❌ Frais d'Annulation</h2>
              <p className="text-gray-600 mb-4">Prise en charge possible par l'assurance selon le motif :</p>
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-3 bg-red-50 rounded">
                  <span className="font-bold text-red-600">•</span>
                  <p className="text-gray-700"><strong>Jusqu'à 179 jours avant :</strong> Retenue de 20% du forfait + adhésion + assurance</p>
                </div>
                <div className="flex items-start gap-3 p-3 bg-red-50 rounded">
                  <span className="font-bold text-red-600">•</span>
                  <p className="text-gray-700"><strong>De 180 à 46 jours avant :</strong> Retenue de 50% du forfait + adhésion + assurance</p>
                </div>
                <div className="flex items-start gap-3 p-3 bg-red-50 rounded">
                  <span className="font-bold text-red-600">•</span>
                  <p className="text-gray-700"><strong>Moins de 45 jours avant :</strong> Retenue de 100% du forfait + adhésion + assurance</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="section bg-gradient-to-r from-[#5BC5D4] to-[#3BA4B3] text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-bold mb-4" data-testid="faq-contact-titre">{T('faq_contact_titre')}</h2>
          <p className="text-xl mb-8 opacity-90">{T('faq_contact_sous_titre')}</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="https://wa.me/message/XTYPH2HUY3L6L1" target="_blank" rel="noopener noreferrer" className="btn bg-white text-[#5BC5D4] hover:bg-gray-100">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              WhatsApp
            </a>
            <a href="mailto:rhoneamitie@gmail.com" className="btn btn-outline border-white text-white hover:bg-white hover:text-[#5BC5D4]">
              <svg className="w-5 h-5 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
              rhoneamitie@gmail.com
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HowToRegister;